import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {RentalOrderResponseBody, useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {buildArray, composePath} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';
import {resolvePairedRecordCode} from './resolvePairedRecordCode';

// Reuse the rental-order state labels already defined for the kanban board.
const getStateLabel = (state: RentalOrderResponseBody['state']) =>
  match(state)
    .with('draft', () => i18n.t('page.rentalOrder.labels.draft'))
    .with('reserved', () => i18n.t('page.rentalOrder.labels.reserved'))
    .with('rented', () => i18n.t('page.rentalOrder.labels.rented'))
    .with('returned', () => i18n.t('page.rentalOrder.labels.returned'))
    .with('closed', () => i18n.t('page.rentalOrder.labels.closed'))
    .with('canceled', () => i18n.t('page.rentalOrder.labels.cancelled'))
    .exhaustive();

export function useRentalOrderPairedRecord(rentalOrderId: string | null): PairedRecordView {
  const {data: rentalOrder, isLoading} = useGetRentalOrderQuery(
    isNotNil(rentalOrderId) ? {rentalOrderId} : skipToken
  );

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(rentalOrder?.customerId) ? {customerId: rentalOrder.customerId} : skipToken
  );

  const customerName = getCustomerName(customer);

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(
      isNotNil(rentalOrder?.state),
      `${i18n.t('general.labels.status')}: ${isNotNil(rentalOrder?.state) ? getStateLabel(rentalOrder.state) : ''}`
    );

  return {
    isLoading,
    code: resolvePairedRecordCode(rentalOrder?.number, isLoading),
    detailPath: isNotNil(rentalOrderId)
      ? composePath(rentalOrderRoutes.detail, {params: {id: rentalOrderId}})
      : null,
    parameters,
    vehicleIds: isNotNil(rentalOrder?.rentalVehicleId) ? [rentalOrder.rentalVehicleId] : [],
  };
}
